import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { addMonths, format, isSameDay } from 'date-fns'
import {
  ArrowLeft,
  CalendarIcon,
  ChevronDown,
  ChevronUp,
  Gift,
} from 'lucide-react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Label } from '@/components/ui/label'
import SlotsPanel from '@/components/experiences/experience-slots-panel'
import { PopoverClose } from '@radix-ui/react-popover'
import { ExperienceOptionService } from '@/lib/services/experience_option'
import { FormikProps } from 'formik'
import {
  CreateOrderRequest,
  Experience,
  ExperienceOption,
  Order,
} from '@/types'
import { useTranslation } from 'next-i18next'
import { ExperienceService } from '@/lib/services/experience'
import { BOOKING_HORIZON_MONTHS } from '@/lib/constants/booking'
import { useToast } from '@/hooks/use-toast'
import { formatPrice } from '@/helper/formatter'
import { keyBy, sum } from 'lodash-es'
import { Badge } from '@/components/ui/badge'
import { Separator } from '../ui/separator'
import ExperiencePickupField from './experience-pickup-field'
import ErrorMessage from '../ui/error-message'
import PriceCard from './price-card'
import { getSeasonalPrice, isFreeExperience, shouldHideFreeLabel } from '@/helper/utils'
import { LoadingSpinner } from '../layout/loading-spinner'
import { useRouter } from 'next/router'

import { dateLocales } from '../language-switcher'
import ExperienceOptionFromPrice from './experience-option-from-price'
import { OrderService } from '@/lib/services/order'
import { ORDER_SOURCE_EXTERNAL_API } from '@/types/voucher'

interface Props {
  experience: Experience
  onSubmitButtonClick?: (e: React.MouseEvent<HTMLButtonElement>) => void
  bookSuccess?: boolean
  redirectToCheckout?: () => void
  voucherOrder: Order | undefined
  /**
   * DEV-730: operator "Book Now" flow opts in to bypass the experience cutoff
   * window. Defaults to false so public store/widget pages always enforce it.
   */
  bypassCutoff?: boolean
}

const ExperienceBookingForm = ({
  values,
  errors,
  handleSubmit,
  isSubmitting,
  setFieldValue,
  experience,
  onSubmitButtonClick,
  bookSuccess,
  redirectToCheckout,
  voucherOrder,
  bypassCutoff = false,
}: FormikProps<CreateOrderRequest> & Props) => {
  const { t } = useTranslation('experience')
  const { toast } = useToast()
  const router = useRouter()

  const [availableOptions, setAvailableOptions] = useState<ExperienceOption[]>(
    []
  )
  const [availableSlots, setAvailableSlots] = useState<string[]>()
  const [availablePrices, setAvailablePrices] =
    useState<Record<string, number>>()

  const [availableDates, setAvailableDates] = useState<Date[]>([])
  const [isLoadingDates, setIsLoadingDates] = useState(false)

  // DEV-679: per-fetch request ids guard against stale responses. Each
  // effect bumps its counter before firing and bails after the await if a
  // newer request has started, so a late `[]` cannot overwrite fresh data.
  const optionsRequestId = useRef(0)
  const slotsRequestId = useRef(0)
  const pricesRequestId = useRef(0)

  // Combo option state
  const [comboSelections, setComboSelections] = useState<
    Record<number, { date: Date | undefined; slot: string | undefined }>
  >({})
  const [comboComponentSlots, setComboComponentSlots] = useState<
    Record<number, string[]>
  >({})
  const [comboComponentAvailability, setComboComponentAvailability] = useState<
    Record<number, number | undefined>
  >({})

  const isFree = isFreeExperience(experience)

  const [displayBookSuccess, setDisplayBookSuccess] = useState(false)
  const [showAllOptions, setShowAllOptions] = useState(false)

  const locale = dateLocales[router.locale ?? 'it']

  const hasVoucherMode =
    experience.enableBuyAsVoucher && !experience.onlyBuyAsVoucher

  useEffect(() => {
    if (bookSuccess) {
      setDisplayBookSuccess(true)

      setTimeout(() => {
        setDisplayBookSuccess(false)
      }, 2000)
    }
  }, [bookSuccess])

  const fetchAvailableDates = useCallback(
    async (startDate: Date) => {
      setIsLoadingDates(true)
      try {
        const dates = (
          await ExperienceService.getAvailableDates(
            experience.uuid,
            format(startDate, 'yyyy-MM-dd'),
            router.locale,
            bypassCutoff
          )
        ).map((date: string) => new Date(date))
        setAvailableDates(dates)

        // Pick up the first available date
        setFieldValue('selectedDate', dates[0])
      } catch (e) {
        console.error(e)
        toast({
          title: t('load_failed', { ns: 'common' }),
          variant: 'destructive',
        })
      } finally {
        setIsLoadingDates(false)
      }
    },
    [experience.uuid, router.locale, bypassCutoff]
  )

  const loadAvailablePrices = useCallback(
    async (
      optionId: string,
      date: Date,
      slot: string,
      componentSelections?: { componentOptionId: number; date: string; slot: string }[]
    ) => {
      if (!optionId || !date || !slot) {
        return
      }
      const requestId = ++pricesRequestId.current
      try {
        const prices = await ExperienceOptionService.getAvailablePrices(
          optionId,
          format(date, 'yyyy-MM-dd'),
          slot,
          router.locale,
          componentSelections
        )
        if (requestId !== pricesRequestId.current) return
        setAvailablePrices(prices)
      } catch (e) {
        if (requestId !== pricesRequestId.current) return
        console.error(e)
        toast({
          title: t('load_failed', { ns: 'common' }),
          variant: 'destructive',
        })
      }
    },
    [t, toast, router.locale]
  )

  const loadAvailableVoucherPrices = useCallback(
    async (optionId: string) => {
      try {
        const prices = await ExperienceOptionService.getAvailableVoucherPrices(
          optionId,
          router.locale
        )
        setAvailablePrices(prices)
      } catch (e) {
        console.error(e)
      }
    },
    [router.locale]
  )

  useEffect(() => {
    fetchAvailableDates(new Date())
  }, [fetchAvailableDates])

  useEffect(() => {
    if (!voucherOrder) {
      return
    }

    const selectedPrices: Record<number, number> = {}

    voucherOrder.items.forEach((item) => {
      selectedPrices[item.priceId as number] = item.quantity
    })

    setFieldValue('selectedPrices', selectedPrices)
    setFieldValue('originalOrderId', voucherOrder.id)
  }, [voucherOrder, setFieldValue])

  const updateAvailableSlots = (slots: string[]) => {
    if (values.isBuyingVoucher) {
      return
    }

    setAvailableSlots(slots)

    if (slots && slots.length > 0) {
      setFieldValue('startsSlot', slots[0])
    } else {
      setFieldValue('startsSlot', undefined)
    }
  }

  useEffect(() => {
    const requestId = ++optionsRequestId.current

    const checkAvailableOptions = async (date: Date | undefined) => {
      if (values.isBuyingVoucher) {
        setAvailableOptions(experience.options)
        return
      }

      if (!date) {
        setAvailableOptions([])
        return
      }

      try {
        const options = await ExperienceService.getAvailableOptions(
          experience,
          format(date, 'yyyy-MM-dd'),
          router.locale,
          bypassCutoff
        )

        if (requestId !== optionsRequestId.current) return
        setAvailableOptions(options)
      } catch (e) {
        if (requestId !== optionsRequestId.current) return
        console.error(e)
        toast({
          title: t('load_failed', { ns: 'common' }),
          variant: 'destructive',
        })

        return []
      }
    }

    checkAvailableOptions(values.selectedDate)
    // Key on experience.uuid (not the whole object) so a parent re-render
    // with a new reference does not refetch and reset the user's choices.
  }, [values.selectedDate, router.locale, experience.uuid, experience.options, values.isBuyingVoucher, bypassCutoff])

  useEffect(() => {
    if (voucherOrder) {
      setFieldValue('selectedOption', String(voucherOrder.option.id))
      return
    }

    if (availableOptions.length <= 0) {
      return
    }

    // DEV-679: keep the user's choice on refetch. Only auto-select when no
    // option is set, or when the current one is gone from the new list.
    const currentId = values.selectedOption
      ? Number(values.selectedOption)
      : undefined
    if (currentId !== undefined && availableOptions.some((o) => o.id === currentId)) {
      return
    }

    setFieldValue('selectedOption', String(availableOptions[0].id))
  }, [availableOptions, setFieldValue, voucherOrder, values.selectedOption])

  useEffect(() => {
    if (!values.selectedOption || !values.selectedDate) {
      return
    }

    // Skip for combo options — combo components load their own slots
    const selectedOpt = availableOptions.find((o) => o.id === Number(values.selectedOption))
    if (selectedOpt?.isCombo) return

    const requestId = ++slotsRequestId.current

    const checkAvailableSlots = async (optionId: string, date: string) => {
      try {
        const slots = await ExperienceOptionService.getAvailableSlots(
          optionId,
          date,
          router.locale,
          bypassCutoff
        )

        if (requestId !== slotsRequestId.current) return
        updateAvailableSlots(slots)
      } catch (e) {
        if (requestId !== slotsRequestId.current) return
        console.error(e)
        toast({
          title: t('load_failed', { ns: 'common' }),
          variant: 'destructive',
        })
      }
    }

    checkAvailableSlots(
      values.selectedOption,
      format(values.selectedDate, 'yyyy-MM-dd')
    )
  }, [values.selectedOption, values.selectedDate, router.locale, t, toast, availableOptions, bypassCutoff])

  useEffect(() => {
    if (
      !values.startsSlot ||
      !values.selectedDate ||
      !values.selectedOption ||
      values.selectedOption === ''
    ) {
      return
    }

    // Skip for combo options — combo useEffect handles price loading with componentSelections
    const selectedOpt = availableOptions.find((o) => o.id === Number(values.selectedOption))
    if (selectedOpt?.isCombo) return

    loadAvailablePrices(
      values.selectedOption,
      values.selectedDate,
      values.startsSlot
    )
  }, [
    values.startsSlot,
    values.isBuyingVoucher,
    values.selectedDate,
    values.selectedOption,
    loadAvailablePrices,
    availableOptions,
  ])

  useEffect(() => {
    if (!values.isBuyingVoucher || !values.selectedOption) {
      return
    }

    loadAvailableVoucherPrices(values.selectedOption)
  }, [
    values.selectedOption,
    values.isBuyingVoucher,
    loadAvailableVoucherPrices,
  ])

  const option = useMemo(
    () => availableOptions.find((o) => o.id === Number(values.selectedOption)),
    [values.selectedOption, availableOptions]
  )

  const isComboOption = !!option?.isCombo

  // Reset combo selections when option changes
  useEffect(() => {
    if (isComboOption) {
      setComboSelections({})
      setComboComponentSlots({})
      setComboComponentAvailability({})
    }
  }, [values.selectedOption, isComboOption])

  // Load slots for each combo component when date is selected
  const loadComboComponentSlots = useCallback(
    async (componentOptionId: number, date: Date) => {
      try {
        const slots = await ExperienceOptionService.getAvailableSlots(
          String(componentOptionId),
          format(date, 'yyyy-MM-dd'),
          router.locale,
          bypassCutoff
        )
        setComboComponentSlots((prev) => ({
          ...prev,
          [componentOptionId]: slots,
        }))
      } catch (e) {
        console.error(e)
      }
    },
    [router.locale, bypassCutoff]
  )

  // Load availability for a single combo component
  const loadComboComponentAvailability = useCallback(
    async (componentOptionId: number, date: Date, slot: string) => {
      try {
        const prices = await ExperienceOptionService.getAvailablePrices(
          String(componentOptionId),
          format(date, 'yyyy-MM-dd'),
          slot,
          router.locale
        )
        setComboComponentAvailability((prev) => ({
          ...prev,
          [componentOptionId]: prices[0] ?? 0,
        }))
      } catch (e) {
        console.error(e)
      }
    },
    [router.locale]
  )

  // Check if all combo components have date and slot selected
  const allComboSelectionsComplete = useMemo(() => {
    if (!isComboOption || !option?.componentOptions) return false
    return option.componentOptions.every(
      (c) =>
        comboSelections[c.optionId]?.date && comboSelections[c.optionId]?.slot
    )
  }, [isComboOption, option, comboSelections])

  // Sync combo selections to form values
  useEffect(() => {
    if (!isComboOption || !allComboSelectionsComplete || !option?.componentOptions) return

    const componentSelectionsArray = option.componentOptions.map((c) => ({
      componentOptionId: c.optionId,
      date: format(comboSelections[c.optionId].date!, 'yyyy-MM-dd'),
      slot: comboSelections[c.optionId].slot!,
    }))

    setFieldValue('componentSelections', componentSelectionsArray)

    // DEV-703 — surface the first leg's date as the parent's selectedDate so
    // the submit payload no longer carries the stale Formik default (today).
    // The backend re-derives parent times from componentSelections too; this
    // keeps client-side computed values (e.g. price preview) consistent.
    const first = componentSelectionsArray[0]
    setFieldValue('startsSlot', first.slot)
    setFieldValue(
      'selectedDate',
      comboSelections[option.componentOptions[0].optionId].date,
    )

    // Load available prices for combo with component selections
    loadAvailablePrices(
      String(option.id),
      comboSelections[option.componentOptions[0].optionId].date!,
      first.slot,
      componentSelectionsArray
    )
  }, [allComboSelectionsComplete, comboSelections, isComboOption, option])

  const extras = useMemo(
    () => option?.prices.filter((option) => option.isExtra) ?? [],
    [option]
  )

  const rates = useMemo(
    () => option?.prices.filter((option) => !option.isExtra) ?? [],
    [option]
  )

  const isIndividualStock = !!(option && option.maxParticipants === -1)

  const remainGroupStock = useMemo(() => {
    if (!option) {
      return 0
    }

    if (option.maxParticipants === -1) {
      return 0
    }

    const notExtraPrices: Record<string, number> = {}

    Object.keys(values.selectedPrices).forEach((key) => {
      if (!extras.some((extra) => extra.id === Number(key))) {
        notExtraPrices[key] = values.selectedPrices[key]
      }
    })

    return (availablePrices?.[0] ?? 0) - sum(Object.values(notExtraPrices))
  }, [option, values.selectedPrices, availablePrices, extras])

  const quantityByProductId = useMemo(() => {
    const map: Record<number, number> = {}

    // DEV-661: extras backed by inventoried products must also contribute to
    // the per-product quantity total so the cart selector caps at the real
    // inventory. The previous version iterated only `rates` and an extra's
    // selected quantity stayed invisible to the cap logic.
    const allPrices = option?.prices ?? []

    Object.keys(values.selectedPrices).forEach((key) => {
      const price = allPrices.find((p) => p.id === Number(key))

      if (price && price.productId) {
        map[price.productId as any] =
          (map[price.productId as any] ?? 0) + values.selectedPrices[key]
      }
    })

    return map
  }, [option, values.selectedPrices])

  const totalAmount = useMemo(() => {
    const priceById = keyBy(option?.prices, 'id')

    return Object.keys(values.selectedPrices).reduce(
      (sum, priceId) =>
        sum +
        (values.isBuyingVoucher
          ? priceById[priceId].retailRate
          : getSeasonalPrice(priceById[priceId], values.selectedDate)) *
          values.selectedPrices[priceId],
      0
    )
  }, [
    option,
    values.selectedPrices,
    values.selectedDate,
    values.isBuyingVoucher,
  ])

  const hideFreeTotal = useMemo(
    () => totalAmount < 0.01 && option?.prices ? shouldHideFreeLabel(option.prices) : false,
    [totalAmount, option]
  )

  const isBlocking = router.query.isBlocking === '1'

  const handleCancelAndRefund = async () => {
    if (!voucherOrder) {
      return
    }

    if (!window.confirm(t('common:irreversible_warning'))) {
      return
    }

    try {
      const canceledOrder = await OrderService.markAsCanceled(voucherOrder.id)

      toast({ title: t('common:save_success'), variant: 'success' })

      window.location.reload()
    } catch (e: any) {
      toast({
        title: t('save_failed', { ns: 'common' }),
        variant: 'destructive',
      })
    }
  }

  // Voucher redemption mode
  if (voucherOrder) {
    return (
      <form
        translate="no"
        className="rounded-md border border-1 p-6 space-y-4 sticky top-24 max-h-[calc(100vh-120px)] overflow-auto notranslate"
        onSubmit={handleSubmit}
      >
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant={'outline'}
              className="justify-start text-left font-normal w-full mt-2"
            >
              <CalendarIcon />
              {values.selectedDate ? (
                format(values.selectedDate, 'PPP', {
                  locale,
                })
              ) : (
                <span>{t('select_date')}</span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent
            translate="no"
            className="w-auto p-0 notranslate"
            align="start"
          >
            <Calendar
              mode="single"
              numberOfMonths={1}
              selected={values.selectedDate}
              required
              locale={locale}
              fromDate={new Date()}
              toDate={addMonths(new Date(), BOOKING_HORIZON_MONTHS)}
              disabled={(date) => {
                if (isLoadingDates) return true
                return !availableDates.some((availableDate) =>
                  isSameDay(availableDate, date)
                )
              }}
              onMonthChange={(month) => {
                fetchAvailableDates(month)
              }}
              onSelect={async (value) => {
                if (!value) return

                updateAvailableSlots([])
                setFieldValue('selectedDate', value)
                document.getElementById('close-calendar')?.click()
              }}
              initialFocus
            />

            <PopoverClose
              className="PopoverClose"
              aria-label="Close"
              id="close-calendar"
            />
          </PopoverContent>
        </Popover>

        {isLoadingDates && (
          <LoadingSpinner
            fullscreen
            className="absolute bg-slate-50 opacity-80 top-0 left-0 h-full w-full"
          />
        )}

        <fieldset>
          <Label>{t('option')}</Label>

          <div className="space-y-2">
            <ExperienceOptionFromPrice
              option={voucherOrder.option}
              values={values}
              setFieldValue={setFieldValue}
              hidePrice
            />
          </div>
        </fieldset>

        {values.selectedOption && (availableSlots?.length ?? 0) > 0 && (
          <fieldset>
            <Label>{t('start_time')}</Label>

            <SlotsPanel
              availableSlots={availableSlots ?? []}
              selectedSlot={values.startsSlot}
              onSlotChanged={(slot) => {
                setFieldValue('startsSlot', slot)
              }}
              openHours={option?.openHours}
            />
          </fieldset>
        )}

        {values.startsSlot ? (
          <>
            {rates.length > 0 && (
              <fieldset>
                <Label>{t('units')}</Label>

                {rates.map((price) => (
                  <PriceCard
                    selectedDate={values.selectedDate!}
                    key={price.id}
                    values={values}
                    setFieldValue={setFieldValue}
                    availablePrices={availablePrices}
                    price={price}
                    quantityByProductId={quantityByProductId}
                    isIndividualStock={isIndividualStock}
                    remainGroupStock={remainGroupStock}
                    actionable={false}
                    isBlocking={false}
                    hidePrice
                  />
                ))}
                <ErrorMessage message={errors?.selectedPrices} />
              </fieldset>
            )}

            {extras.length > 0 && (
              <fieldset>
                <Label>{t('add_extra')}</Label>

                {extras.map((price) => (
                  <PriceCard
                    selectedDate={values.selectedDate!}
                    key={price.id}
                    values={values}
                    setFieldValue={setFieldValue}
                    availablePrices={availablePrices}
                    price={price}
                    quantityByProductId={quantityByProductId}
                    isIndividualStock={isIndividualStock}
                    remainGroupStock={remainGroupStock}
                    isExtra
                    isBlocking={false}
                    actionable={!experience.isInformationPage}
                    hidePrice
                  />
                ))}
                <ErrorMessage message={errors.selectedPrices} />
              </fieldset>
            )}

            <ExperiencePickupField
              experience={experience}
              selectedOption={option}
              values={values}
              setFieldValue={setFieldValue}
              actionable={!isBlocking}
            />
            <ErrorMessage message={errors?.selectedPoint} />

            {experience.isInformationPage ? null : (
              <>
                <Separator />

                {/* Voucher mode: no total — the guest already paid via the
                    bundle, showing "Gratuito" / "0€" is misleading. We
                    surface the voucher code instead so the redeemer can
                    cross-check before confirming. */}
                {voucherOrder?.createdVoucher?.code && (
                  <div className="flex justify-between text-sm py-2">
                    <span className="text-muted-foreground">
                      {t('redeem:voucher_code')}
                    </span>
                    <span className="font-mono">
                      {voucherOrder.createdVoucher.code}
                    </span>
                  </div>
                )}

                <Button
                  translate="no"
                  className="w-full uppercase notranslate"
                  disabled={isSubmitting}
                  id="add-to-cart-button"
                  onClick={onSubmitButtonClick}
                >
                  {t('redeem:confirm_redemption_cta')}
                </Button>

                {/* Cancel-and-refund is for legacy internal vouchers (user
                    paid Aloe directly and may want a refund before redeeming).
                    For BOL/external_api vouchers the customer paid HC, not
                    Aloe — there is nothing to refund and the admin endpoint
                    would 401 for the anonymous redeemer. Check on the order's
                    own source: the verify response doesn't eager-load
                    createdVoucher (returns null), so the previous nested
                    check was always false → button always rendered. */}
                {voucherOrder?.source !== ORDER_SOURCE_EXTERNAL_API && (
                  <Button
                    translate="no"
                    variant="ghost"
                    type="button"
                    className="w-full notranslate"
                    onClick={handleCancelAndRefund}
                  >
                    {t('common:cancel_and_refund')}
                  </Button>
                )}
              </>
            )}
          </>
        ) : null}
      </form>
    )
  }

  // Voucher mode
  if (values.isBuyingVoucher) {
    return (
      <form
        translate="no"
        className="rounded-md border border-1 p-6 space-y-4 sticky top-24 max-h-[calc(100vh-120px)] overflow-auto notranslate"
        onSubmit={handleSubmit}
      >
        <div className="">
          <h2 className="text-xl font-bold flex items-center gap-1">
            <Gift className="h-5 w-5" />
            {t('voucher')}
          </h2>
          <p className="text-sm text-muted-foreground mt-3">
            {t('voucher_date_time_description')}
          </p>
        </div>
        <fieldset>
          <Label>{t('option')}</Label>

          <div className="space-y-2">
            {availableOptions && availableOptions.length > 0 ? (
              <>
                {(showAllOptions
                  ? availableOptions
                  : availableOptions.slice(0, 3)
                ).map((option) => (
                  <ExperienceOptionFromPrice
                    key={option.id}
                    option={option}
                    values={values}
                    setFieldValue={setFieldValue}
                  />
                ))}

                {availableOptions.length > 3 && (
                  <div className="flex justify-center">
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => setShowAllOptions(!showAllOptions)}
                    >
                      {showAllOptions ? (
                        <div className="flex items-center justify-center gap-2">
                          <ChevronUp />
                          {t('common:less')}
                        </div>
                      ) : (
                        <div className="flex items-center justify-center gap-2">
                          <ChevronDown />
                          {t('common:more')}
                        </div>
                      )}
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center text-muted-foreground py-4">
                {t('no_available_option')}
              </div>
            )}
          </div>
        </fieldset>

        {values.startsSlot ? (
          <>
            {rates.length > 0 && (
              <fieldset>
                <Label>{t('units')}</Label>

                {rates.map((price) => (
                  <PriceCard
                    selectedDate={values.selectedDate!}
                    key={price.id}
                    values={values}
                    setFieldValue={setFieldValue}
                    availablePrices={availablePrices}
                    price={price}
                    quantityByProductId={quantityByProductId}
                    isIndividualStock={isIndividualStock}
                    remainGroupStock={remainGroupStock}
                    isBlocking={isBlocking}
                    actionable={!experience.isInformationPage}
                  />
                ))}
                <ErrorMessage message={errors?.selectedPrices} />
              </fieldset>
            )}

            {extras.length > 0 && (
              <fieldset>
                <Label>{t('add_extra')}</Label>

                {extras.map((price) => (
                  <PriceCard
                    selectedDate={values.selectedDate!}
                    key={price.id}
                    values={values}
                    setFieldValue={setFieldValue}
                    availablePrices={availablePrices}
                    price={price}
                    quantityByProductId={quantityByProductId}
                    isIndividualStock={isIndividualStock}
                    remainGroupStock={remainGroupStock}
                    isExtra
                    isBlocking={isBlocking}
                    actionable={!experience.isInformationPage}
                  />
                ))}
                <ErrorMessage message={errors.selectedPrices} />
              </fieldset>
            )}

            {experience.isInformationPage ? null : (
              <>
                <Separator />
                {!isBlocking && (
                  <fieldset>
                    {!hideFreeTotal && (
                      <h2 className="text-xl font-bold flex justify-between">
                        <span>{t('total')}</span>

                        <span>
                          {Object.keys(values.selectedPrices ?? {}).length > 0 &&
                          totalAmount < 0.01
                            ? t('common:free')
                            : formatPrice(totalAmount)}
                        </span>
                      </h2>
                    )}
                  </fieldset>
                )}

                <Button
                  translate="no"
                  className="w-full uppercase notranslate"
                  disabled={isSubmitting}
                  id="add-to-cart-button"
                  onClick={onSubmitButtonClick}
                >
                  {isFree ? t('common:book') : t('add_to_cart')}
                </Button>

                {displayBookSuccess && (
                  <div className="text-sm text-primary">
                    {t('add_to_cart_success')}
                  </div>
                )}

                {redirectToCheckout && bookSuccess ? (
                  <Button
                    translate="no"
                    variant="outline"
                    type="button"
                    className="w-full uppercase notranslate"
                    onClick={redirectToCheckout}
                  >
                    {t('go_to_checkout')}
                  </Button>
                ) : null}

                {!experience.onlyBuyAsVoucher && (
                  <Button
                    className="w-full"
                    variant="outline"
                    onClick={() => {
                      setFieldValue('isBuyingVoucher', false)
                      setFieldValue('selectedPrices', {})
                      setFieldValue('selectedOption', undefined)

                      fetchAvailableDates(new Date())
                    }}
                  >
                    <ArrowLeft className="h-4 w-4" />
                    {t('book_with_date_and_time')}
                  </Button>
                )}
              </>
            )}
          </>
        ) : null}
      </form>
    )
  }

  // Regular mode
  return (
    <form
      translate="no"
      className="rounded-md border border-1 p-6 space-y-4 sticky top-24 max-h-[calc(100vh-120px)] overflow-auto notranslate"
      onSubmit={handleSubmit}
    >
      {!isComboOption && (
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant={'outline'}
              className="justify-start text-left font-normal w-full mt-2"
            >
              <CalendarIcon />
              {values.selectedDate ? (
                format(values.selectedDate, 'PPP', {
                  locale,
                })
              ) : (
                <span>{t('select_date')}</span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent
            translate="no"
            className="w-auto p-0 notranslate"
            align="start"
          >
            <Calendar
              mode="single"
              numberOfMonths={1}
              selected={values.selectedDate}
              required
              locale={locale}
              fromDate={new Date()}
              toDate={addMonths(new Date(), BOOKING_HORIZON_MONTHS)}
              disabled={(date) => {
                if (isLoadingDates) return true
                return !availableDates.some((availableDate) =>
                  isSameDay(availableDate, date)
                )
              }}
              onMonthChange={(month) => {
                fetchAvailableDates(month)
              }}
              onSelect={async (value) => {
                if (!value) return

                updateAvailableSlots([])
                setFieldValue('selectedOption', '')
                setFieldValue('selectedPrices', {})

                setFieldValue('selectedDate', value)
                document.getElementById('close-calendar')?.click()
              }}
              initialFocus
            />

            <PopoverClose
              className="PopoverClose"
              aria-label="Close"
              id="close-calendar"
            />
          </PopoverContent>
        </Popover>
      )}

      {isLoadingDates && (
        <LoadingSpinner
          fullscreen
          className="absolute bg-slate-50 opacity-80 top-0 left-0 h-full w-full"
        />
      )}

      <fieldset>
        <Label>{t('option')}</Label>

        <div className="space-y-2">
          {availableOptions && availableOptions.length > 0 ? (
            <>
              {(showAllOptions
                ? availableOptions
                : availableOptions.slice(0, 3)
              ).map((option) => (
                <ExperienceOptionFromPrice
                  key={option.id}
                  option={option}
                  values={values}
                  setFieldValue={setFieldValue}
                />
              ))}

              {availableOptions.length > 3 && (
                <div className="flex justify-center">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setShowAllOptions(!showAllOptions)}
                  >
                    {showAllOptions ? (
                      <div className="flex items-center justify-center gap-2">
                        <ChevronUp />
                        {t('common:less')}
                      </div>
                    ) : (
                      <div className="flex items-center justify-center gap-2">
                        <ChevronDown />
                        {t('common:more')}
                      </div>
                    )}
                  </Button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center text-muted-foreground py-4">
              {t('no_available_option')}
            </div>
          )}
        </div>
      </fieldset>

      {values.selectedOption && !isComboOption && (availableSlots?.length ?? 0) > 0 && (
        <fieldset>
          <Label>{t('start_time')}</Label>

          <SlotsPanel
            availableSlots={availableSlots ?? []}
            selectedSlot={values.startsSlot}
            onSlotChanged={(slot) => {
              setFieldValue('startsSlot', slot)
              setFieldValue('selectedPrices', {})
            }}
            openHours={option?.openHours}
          />
        </fieldset>
      )}

      {/* Combo option: per-component date/time selection */}
      {values.selectedOption && isComboOption && option?.componentOptions && (
        <div className="space-y-4">
          {option.componentOptions
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map((component) => (
              <fieldset key={component.optionId} className="border rounded-md p-3 space-y-2">
                <Label className="font-semibold">{component.label}</Label>

                {/* Date picker for this component */}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="justify-start text-left font-normal w-full"
                      type="button"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {comboSelections[component.optionId]?.date
                        ? format(comboSelections[component.optionId].date!, 'PPP', { locale })
                        : t('select_date_for', { label: component.label })}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      numberOfMonths={1}
                      selected={comboSelections[component.optionId]?.date}
                      required
                      locale={locale}
                      fromDate={new Date()}
                      toDate={addMonths(new Date(), BOOKING_HORIZON_MONTHS)}
                      disabled={(date) => {
                        if (isLoadingDates) return true
                        return !availableDates.some((d) => isSameDay(d, date))
                      }}
                      onSelect={(date) => {
                        if (!date) return
                        setComboSelections((prev) => ({
                          ...prev,
                          [component.optionId]: { date, slot: undefined },
                        }))
                        setComboComponentAvailability((prev) => ({
                          ...prev,
                          [component.optionId]: undefined,
                        }))
                        loadComboComponentSlots(component.optionId, date)
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>

                {/* Slot picker for this component */}
                {comboSelections[component.optionId]?.date && (comboComponentSlots[component.optionId]?.length ?? 0) > 0 && (
                  <SlotsPanel
                    availableSlots={comboComponentSlots[component.optionId] ?? []}
                    selectedSlot={comboSelections[component.optionId]?.slot}
                    onSlotChanged={(slot) => {
                      setComboSelections((prev) => ({
                        ...prev,
                        [component.optionId]: {
                          ...prev[component.optionId],
                          slot,
                        },
                      }))
                      setFieldValue('selectedPrices', {})
                      const date = comboSelections[component.optionId]?.date
                      if (date) {
                        loadComboComponentAvailability(component.optionId, date, slot)
                      }
                    }}
                    openHours={component.option?.openHours}
                  />
                )}

                {comboComponentAvailability[component.optionId] !== undefined && (
                  comboComponentAvailability[component.optionId] === 0 ? (
                    <Badge variant="destructive">{t('sold_out')}</Badge>
                  ) : (
                    <Badge variant="outline" className="border-green-600 text-green-600">
                      {t('spots_available', { count: comboComponentAvailability[component.optionId] })}
                    </Badge>
                  )
                )}
              </fieldset>
            ))}
        </div>
      )}

      {(isComboOption ? allComboSelectionsComplete : values.startsSlot) ? (
        <>
          {rates.length > 0 && (
            <fieldset>
              <Label>{t('units')}</Label>

              {rates.map((price) => (
                <PriceCard
                  selectedDate={values.selectedDate!}
                  key={price.id}
                  values={values}
                  setFieldValue={setFieldValue}
                  availablePrices={availablePrices}
                  price={price}
                  quantityByProductId={quantityByProductId}
                  isIndividualStock={isIndividualStock}
                  remainGroupStock={remainGroupStock}
                  isBlocking={isBlocking}
                  actionable={!experience.isInformationPage}
                />
              ))}
              <ErrorMessage message={errors?.selectedPrices} />
            </fieldset>
          )}

          {extras.length > 0 && (
            <fieldset>
              <Label>{t('add_extra')}</Label>

              {extras.map((price) => (
                <PriceCard
                  selectedDate={values.selectedDate!}
                  key={price.id}
                  values={values}
                  setFieldValue={setFieldValue}
                  availablePrices={availablePrices}
                  price={price}
                  quantityByProductId={quantityByProductId}
                  isIndividualStock={isIndividualStock}
                  remainGroupStock={remainGroupStock}
                  isExtra
                  isBlocking={isBlocking}
                  actionable={!experience.isInformationPage}
                />
              ))}
              <ErrorMessage message={errors.selectedPrices} />
            </fieldset>
          )}

          <ExperiencePickupField
            experience={experience}
            selectedOption={option}
            values={values}
            setFieldValue={setFieldValue}
            actionable={!isBlocking}
          />
          <ErrorMessage message={errors?.selectedPoint} />

          {experience.isInformationPage ? null : (
            <>
              <Separator />
              {!isBlocking && (
                <fieldset>
                  {!hideFreeTotal && (
                    <h2 className="text-xl font-bold flex justify-between">
                      <span>{t('total')}</span>

                      <span>
                        {Object.keys(values.selectedPrices ?? {}).length > 0 &&
                        totalAmount < 0.01
                          ? t('common:free')
                          : formatPrice(totalAmount)}
                      </span>
                    </h2>
                  )}
                </fieldset>
              )}

              {isBlocking ? (
                <Button
                  className="w-full uppercase"
                  variant="destructive"
                  disabled={isSubmitting}
                >
                  {t('stop_bookings')}
                </Button>
              ) : (
                <>
                  <Button
                    translate="no"
                    className="w-full uppercase notranslate"
                    disabled={isSubmitting}
                    id="add-to-cart-button"
                    onClick={onSubmitButtonClick}
                  >
                    {isFree ? t('common:book') : t('add_to_cart')}
                  </Button>

                  {displayBookSuccess && (
                    <div className="text-sm text-primary">
                      {t('add_to_cart_success')}
                    </div>
                  )}

                  {redirectToCheckout && bookSuccess ? (
                    <Button
                      translate="no"
                      variant="outline"
                      type="button"
                      className="w-full uppercase notranslate"
                      onClick={redirectToCheckout}
                    >
                      {t('go_to_checkout')}
                    </Button>
                  ) : null}
                </>
              )}
            </>
          )}
        </>
      ) : null}

      {hasVoucherMode && (
        <>
          <Separator />
          <Button
            variant="secondary"
            className="w-full border h-16"
            onClick={() => {
              setFieldValue('isBuyingVoucher', true)
              setFieldValue('selectedDate', new Date())

              setFieldValue('selectedPrices', {})
              setFieldValue('selectedOption', undefined)
              setFieldValue('startsSlot', '00:00')
            }}
          >
            <div className="space-y-1">
              <div className="flex items-center gap-2 justify-center">
                <Gift className="h-5 w-5" />
                <h3 className="text-sm font-bold">{t('buy_a_voucher')}</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                {t('no_date_needed')}
              </p>
            </div>
          </Button>
        </>
      )}
    </form>
  )
}

export default ExperienceBookingForm
